import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { CssBaseline } from "@mui/material";
import CreateEmployeeForm from "./components/employees/CreateEmployeeForm";
import ListEmployees from "./components/employees/ListEmployees";
import EmployeeDetails from "./components/employees/EmployeeDetails";
import LayoutWrapper from "./components/LayoutWrapper";
import MachineList from "./components/machines/MachineList";
import CreateMachine from "./components/machines/CreateMachine";
import MachineDetails from "./components/machines/MachineDetails";
import CompanyDetails from "./components/companies/CompanyDetails";
import CompanyList from "./components/companies/CompanyList";
import CreateCompany from "./components/companies/CreateCompany";
import ReceivedNoteList from "./components/received-notes/ReceivedNoteList";
import CreateReceivedNote from "./components/received-notes/CreateReceivedNote";
import ReceivedNoteDetails from "./components/received-notes/ReceivedNoteDetails";
import ProgramList from "./components/programs/ProgramList";
import CreateProgram from "./components/programs/CreateProgram";
import ProgramDetails from "./components/programs/ProgramDetails";
import DeliveryList from "./components/deliveries/DeliveryList";
import CreateDelivery from "./components/deliveries/CreateDelivery";
import DeliveryDetails from "./components/deliveries/DeliveryDetails";
import EmployeeShiftList from "./components/employee-shifts/EmployeeShiftList";
import CreateEmployeeShift from "./components/employee-shifts/CreateEmployeeShift";
import EmployeeShiftDetails from "./components/employee-shifts/EmployeeShiftDetails";
import InventoryList from "./components/inventory/InventoryList";
import Login from "./components/users/Login";
import ForgotPassword from "./components/users/ForgotPassword";
import ResetPassword from "./components/users/ResetPassword";
import UserList from "./components/users/UserList";
import CreateUser from "./components/users/CreateUser";
import InviteUser from "./components/users/InviteUser";
import UserDetails from "./components/users/UserDetails";
import { ProtectedRoute } from "./components/ProtectedRoute";
import Home from "./components/home/Home";
import ProductionMetrics from "./components/metrics/ProductionMetrics";
import EmployeeMetrics from "./components/metrics/EmployeeMetrics";
import ViewAnalytics from "./components/metrics/ViewAnalytics";
import PublicProgramView from "./components/public/PublicProgramView";
import OrgSettings from "./components/settings/OrgSettings";
import InvoiceList from "./components/invoices/InvoiceList";
import CreateInvoice from "./components/invoices/CreateInvoice";
import InvoiceDetails from "./components/invoices/InvoiceDetails";
import Finance from "./components/finance/Finance";
import AuditLogList from "./components/audit-logs/AuditLogList";

const App = () => {
  return (
    <BrowserRouter>
      <CssBaseline />
      <Routes>
        {/* Public routes (no authentication or layout) */}
        <Route path="/p/:shareToken" element={<PublicProgramView />} />
        {/* Legacy longer route - keep for backwards compatibility */}
        <Route
          path="/shared/program/:shareToken"
          element={<PublicProgramView />}
        />

        {/* Auth routes (no layout) */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Protected routes with AppLayout */}
        <Route element={<LayoutWrapper />}>
          <Route
            path="/users"
            element={
              <ProtectedRoute resource="users">
                <UserList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/new"
            element={
              <ProtectedRoute resource="users">
                <CreateUser />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/invite"
            element={
              <ProtectedRoute resource="users">
                <InviteUser />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/:id"
            element={
              <ProtectedRoute resource="users">
                <UserDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/companies"
            element={
              <ProtectedRoute resource="companies">
                <CompanyList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies/new"
            element={
              <ProtectedRoute resource="companies">
                <CreateCompany />
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies/:id"
            element={
              <ProtectedRoute resource="companies">
                <CompanyDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/employees"
            element={
              <ProtectedRoute resource="employees">
                <ListEmployees />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees/new"
            element={
              <ProtectedRoute resource="employees">
                <CreateEmployeeForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees/:id"
            element={
              <ProtectedRoute resource="employees">
                <EmployeeDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/machines"
            element={
              <ProtectedRoute resource="machines">
                <MachineList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/machines/new"
            element={
              <ProtectedRoute resource="machines">
                <CreateMachine />
              </ProtectedRoute>
            }
          />
          <Route
            path="/machines/:id"
            element={
              <ProtectedRoute resource="machines">
                <MachineDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/received-notes"
            element={
              <ProtectedRoute>
                <ReceivedNoteList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/received-notes/new"
            element={
              <ProtectedRoute>
                <CreateReceivedNote />
              </ProtectedRoute>
            }
          />
          <Route
            path="/received-notes/:id"
            element={
              <ProtectedRoute>
                <ReceivedNoteDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/programs"
            element={
              <ProtectedRoute>
                <ProgramList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/programs/new"
            element={
              <ProtectedRoute>
                <CreateProgram />
              </ProtectedRoute>
            }
          />
          <Route
            path="/programs/:id"
            element={
              <ProtectedRoute>
                <ProgramDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/deliveries"
            element={
              <ProtectedRoute>
                <DeliveryList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/deliveries/new"
            element={
              <ProtectedRoute>
                <CreateDelivery />
              </ProtectedRoute>
            }
          />
          <Route
            path="/deliveries/:id"
            element={
              <ProtectedRoute>
                <DeliveryDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/employee-shifts"
            element={
              <ProtectedRoute resource="employee_shifts">
                <EmployeeShiftList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee-shifts/create"
            element={
              <ProtectedRoute resource="employee_shifts">
                <CreateEmployeeShift />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee-shifts/:id"
            element={
              <ProtectedRoute resource="employee_shifts">
                <EmployeeShiftDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/shifts"
            element={
              <ProtectedRoute resource="employee_shifts">
                <Navigate to="/employee-shifts" replace />
              </ProtectedRoute>
            }
          />

          <Route
            path="/inventory"
            element={
              <ProtectedRoute resource="inventory">
                <InventoryList />
              </ProtectedRoute>
            }
          />
          {/* Metrics routes */}
          <Route
            path="/metrics"
            element={
              <ProtectedRoute resource="metrics">
                <Navigate to="/metrics/production" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/metrics/production"
            element={
              <ProtectedRoute resource="metrics">
                <ProductionMetrics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/metrics/employee"
            element={
              <ProtectedRoute resource="metrics">
                <EmployeeMetrics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/metrics/view-analytics"
            element={
              <ProtectedRoute resource="metrics">
                <ViewAnalytics />
              </ProtectedRoute>
            }
          />
          {/* Legacy route for backwards compatibility */}
          <Route
            path="/employee-metrics"
            element={
              <ProtectedRoute resource="metrics">
                <Navigate to="/metrics/employee" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <InvoiceList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/new"
            element={
              <ProtectedRoute>
                <CreateInvoice />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id"
            element={
              <ProtectedRoute>
                <InvoiceDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance"
            element={
              <ProtectedRoute resource="finance">
                <Finance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-log"
            element={
              <ProtectedRoute resource="audit_log">
                <AuditLogList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute resource="settings">
                <OrgSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default App;
